import { isImageDownloadable } from '@/utils/image';
import { z } from 'zod';

export const showInPagesArray = [
  { label: 'PIP', value: 'pip' },
  { label: 'PLP', value: 'plp' },
  { label: 'Content Page', value: 'content-page' },
];

export const supportedDevicesArray = [
  { label: 'Mobile', value: 'mobile' },
  { label: 'Tablet', value: 'tablet' },
  { label: 'Desktop', value: 'desktop' },
];

export const contentPageVariantsArray = [
  { name: 'Inject Current Page', value: 'inject_current_page' },
  { name: 'Banners with External Link', value: 'banners_with_external_link' },
  {
    name: 'Banners with Video and External Link',
    value: 'banners_with_video_and_external_link',
  },
];

export const isUrlValid = async (val: string): Promise<boolean> => {
  const imageExtensions = '(jpe?g|png)';
  const isUrlValid: boolean =
    new RegExp(
      `^https://www\\.ikea\\.com/images/.*\\.${imageExtensions}$`,
      'i',
    ).test(val) ||
    new RegExp(
      `^https://www\\.ikea\\.com/ext/.*\\.${imageExtensions}$`,
      'i',
    ).test(val) ||
    new RegExp(
      `^https://www\\.ikea\\.com/global/.*/images/.*\\.${imageExtensions}$`,
      'i',
    ).test(val) ||
    new RegExp(
      `^https://www\\.ikea\\.com/[a-z]{2}/[a-z]{2}/images/.*\\.${imageExtensions}$`,
      'i',
    ).test(val) ||
    new RegExp(
      `^https://asset\\.inter\\.ikea\\.com/images/.*(\\.${imageExtensions})?$`,
      'i',
    ).test(val);

  if (!isUrlValid) {
    return false;
  }

  const isDownloadable = await isImageDownloadable(val);

  if (!isDownloadable) {
    return false;
  }

  return true;
};

export const plannerCategories = [
  { name: 'Wardrobes and cabinets', value: 'wardrobes-and-cabinets' },
  { name: 'Sofas and chairs', value: 'sofas-and-chairs' },
  { name: 'Rooms', value: 'rooms' },
  { name: 'Other', value: 'other' },
  { name: 'Kreativ', value: 'kreativ' },
];

const isVideoUrlValid = (val: string) => {
  const isUrlValid: boolean =
    /^https:\/\/www\.ikea\.com\/ext\/ingkadam\/.*\.mp4$/i.test(val) ||
    /^https:\/\/www\.ikea\.com\/pvid\/.*\.mp4$/i.test(val);
  return isUrlValid;
};

export function isValidIdentifier(id: string): boolean {
  const regex = /^[a-z]+(-[a-z]+)*$/;
  return regex.test(id);
}

export const PlannerFormSchemaBase = z.object({
  contentPageSectionVisibility: z.object({
    pageSecondaryBanner: z.boolean().optional(),
    pagePrimaryCard: z.boolean().optional(),
    pageSecondaryCard: z.boolean().optional(),
    video: z.boolean().optional(),
  }),
  id: z.string().optional(),
  team: z.string().optional(),
  plannerContact: z
    .string()
    .email('Please enter a valid email address')
    .optional()
    .or(z.literal('')),
  contentPageVariant: z.string().optional(),
  identifier: z.string('Required').min(2, {
    error: 'Identifier must be at least 2 characters.',
  }),
  imageUrl: z.string('Required').refine(async (val) => {
    return isUrlValid(val);
  }, 'Please enter a valid image URL from DAM or IDAM.'),
  secondaryBannerImgUrl: z
    .string()
    .optional()
    .refine(async (val) => {
      if (!val) {
        return true;
      }
      return isUrlValid(val);
    }, 'Please enter a valid image URL from DAM or IDAM.'),
  plannerUrl: z
    .string('Required')
    .refine(
      (s) => s.includes('__countryCode__') && s.includes('__languageCode__'),
      'String must contain placeholder __countryCode__ and __languageCode__',
    ),
  searchable: z.boolean().optional(),
  includeDeeplinkParams: z.boolean().optional(),
  includeListingSearchParams: z.boolean().optional(),
  supportedDevices: z
    .array(
      z.custom(
        (val) =>
          typeof val === 'string' &&
          supportedDevicesArray.some((x) => x.value === val),
      ),
    )
    .optional(),
  // This is optional, only due to kreativ-general and kreativ-searchable
  // They use these planners to index kreativ and related keywords
  // but do not display them on any pages
  showInPages: z
    .array(
      z.custom(
        (val) =>
          typeof val === 'string' &&
          showInPagesArray.some((x) => x.value === val),
      ),
    )
    .optional(),
  category: z.string().optional(),
  productsInclude: z.array(z.string()).optional(),
  productsExclude: z.array(z.string()).optional(),
  rangeInclude: z.array(z.string()).optional(),
  rangeExclude: z.array(z.string()).optional(),
  marketData: z
    .array(
      z.object({
        locale: z.string().optional(),
        imageUrl: z
          .string()
          .nullish()
          .refine(async (val) => {
            if (!val) {
              return true;
            }
            return isUrlValid(val);
          }, 'Please enter a valid image URL from DAM or IDAM.')
          .optional(),
        videoUrl: z
          .string()
          .nullish()
          .refine(async (val) => {
            if (!val) {
              return true;
            }
            return isVideoUrlValid(val);
          }, 'Please enter a valid video URL from DAM or Youtube embed.')
          .optional(),
        primaryCardCtaUrl: z.string().nullish().optional(),
        secondaryCardCtaUrl: z.string().nullish().optional(),
        secondaryBannerCtaUrl: z.string().nullish().optional(),
        secondaryBannerImgUrl: z
          .string()
          .nullish()
          .refine(async (val) => {
            if (!val) {
              return true;
            }
            return isUrlValid(val);
          }, 'Please enter a valid secondary image URL from DAM or IDAM.')
          .optional(),
        isActive: z.boolean().optional(),
        bannerLargeMode: z.enum(['IMAGE', 'VIDEO']).optional().default('IMAGE'),
        videoPosterUrl: z
          .string()
          .nullish()
          .refine(async (val) => {
            if (!val) {
              return true;
            }
            return isUrlValid(val);
          }, 'Please enter a valid video poster image URL from DAM or IDAM.')
          .optional(),
        productsInclude: z.array(z.string()).optional(),
        productsExclude: z.array(z.string()).optional(),
        rangeExclude: z.array(z.string()).optional(),
        rangeInclude: z.array(z.string()).optional(),
      }),
    )
    .optional(),
});

const PlannerAddFormSchemaExtended = PlannerFormSchemaBase.extend({
  identifier: z
    .string('Required')
    .min(2, {
      error: 'Identifier must be at least 2 characters.',
    })
    .refine((val) => {
      if (!val) {
        return true;
      }
      return isValidIdentifier(val);
    }, 'Identifier must be lowercase separated with a -, e.g. "my-planner-name"'),
});

export const addContentPageVariantRefinement = (
  schema: typeof PlannerFormSchemaBase | typeof PlannerAddFormSchemaExtended,
) =>
  schema.refine(
    (data) => {
      if (data.showInPages?.includes('content-page')) {
        return contentPageVariantsArray
          .map((x) => x.value)
          .includes(data.contentPageVariant || '');
      }
      return true;
    },
    {
      error:
        "Content page variant is required when content-page is selected in 'Show in pages'",
      path: ['contentPageVariant'],
    },
  );

export const PlannerEditFormSchema = addContentPageVariantRefinement(
  PlannerFormSchemaBase,
);

export const PlannerAddFormSchema = addContentPageVariantRefinement(
  PlannerAddFormSchemaExtended,
);

export const RequiredPhraseStringSchema = z.record(
  z.string().trim().min(1, 'Key cannot be empty'),
  z.string().trim().min(1, 'Value cannot be empty'),
);

export const PlannerFormPhraseStringsSchema = z.object({
  phraseStrings: RequiredPhraseStringSchema,
});
